import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import type WaveSurfer from 'wavesurfer.js';
import WaveSurferPlayer from '@wavesurfer/react';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Divider } from '@flintmint/web-shared/components/divider';

import styles from './context-item.module.scss';

export const AudioPreview = (props: AudioPreviewProps) => {
  const {
    previewUrl,
    waveformSeed,
    canLoadAudio,
    isAudioLoading,
    onReadMore,
    onDurationChange,
    onRequestPlayback,
    onLoadError,
    parsedContent = '',
    playbackEnabled = true,
  } = props;

  // Distinct from `waveSurfer` on purpose: a load in flight and a load that failed both leave no
  // wavesurfer instance, but only the first should keep the Play button disabled — after a failure
  // the button is the user's way to retry.
  const [isWaveLoading, setIsWaveLoading] = useState(true);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [waveSurfer, setWaveSurfer] = useState<WaveSurfer | null>(null);
  // Only a Play press may start playback. previewUrl lives in the parent and outlives this
  // component, so without this latch a remount (or any reload of the same url) would autoplay a
  // recording — and re-download it — with no user action behind it.
  const [isPlaybackRequested, setIsPlaybackRequested] = useState(false);
  const decorativeWaveform = useMemo(() => buildDecorativeWaveform(waveformSeed), [waveformSeed]);

  useEffect(() => {
    if (previewUrl) return;

    waveSurfer?.pause();
    setWaveSurfer(null);
    setIsAudioPlaying(false);
    onDurationChange?.(0);
  }, [previewUrl, onDurationChange, waveSurfer]);

  useEffect(() => {
    if (!previewUrl) return;
    setIsWaveLoading(true);
    // A different recording is loading; the old one is no longer playing, so the control must stop
    // announcing Pause for it.
    setIsAudioPlaying(false);
  }, [previewUrl]);

  // The owner (the preview modal) is fading out; sound must not outlive the visible card.
  useEffect(() => {
    if (!playbackEnabled) waveSurfer?.pause();
  }, [playbackEnabled, waveSurfer]);

  const contentClassName = clsx({
    'ellipsis-text': !!onReadMore,
    [styles.preWrap]: !onReadMore,
  });

  const handlePlayback = () => {
    if (waveSurfer) {
      void waveSurfer.playPause();
      return;
    }

    setIsPlaybackRequested(true);
    void onRequestPlayback?.();
  };

  const handleLoadError = () => {
    setWaveSurfer(null);
    setIsWaveLoading(false);
    setIsAudioPlaying(false);
    setIsPlaybackRequested(false);
    onLoadError?.();
  };

  return (
    <div className={styles.audioPreviewHolder} data-testid="context-item-audio-preview">
      <div className={styles.player}>
        <button
          type="button"
          className={styles.audioPlayButton}
          disabled={!canLoadAudio || isAudioLoading || (!!previewUrl && isWaveLoading)}
          onClick={handlePlayback}
          aria-label={isAudioPlaying ? 'Pause recording' : 'Play recording'}
        >
          <Icon type={isAudioPlaying ? 'pause' : 'play-circle-02'} size={24} />
        </button>
        <div className={styles.audioWaveHolder}>
          {/* Keyed on whether a waveform is actually rendered, not on whether one is loading: a
              recording that failed to load leaves no wavesurfer behind, and keying on the loading
              flag would show its empty container instead of these bars. */}
          {!waveSurfer && (
            <div className={styles.decorativeWaveform} data-testid="context-item-decorative-waveform">
              {decorativeWaveform.map((height, index) => (
                <span key={index} style={{ height }} />
              ))}
            </div>
          )}
          {!!previewUrl && (
            <div className={clsx({ [styles.hiddenWave]: !waveSurfer })}>
              <WaveSurferPlayer
                barGap={1}
                dragToSeek
                height={16}
                barWidth={1}
                barRadius={2}
                url={previewUrl}
                waveColor="#6FA0FE"
                progressColor="#246BFD"
                cursorColor="transparent"
                onPlay={() => setIsAudioPlaying(true)}
                onPause={() => setIsAudioPlaying(false)}
                onFinish={() => setIsAudioPlaying(false)}
                onError={handleLoadError}
                onReady={(ws) => {
                  setWaveSurfer(ws);
                  setIsWaveLoading(false);
                  onDurationChange?.(ws.getDuration());
                  if (!isPlaybackRequested) return;
                  setIsPlaybackRequested(false);
                  // The owner is already fading out (see the pause effect above); a press made before the
                  // recording was ready must not start it now.
                  if (!playbackEnabled) return;
                  // The recording is fetched and decoded by now, so a rejection here is the browser
                  // refusing to start playback this far from the Play press (autoplay policy), not a
                  // load failure. The instance stays; the next press reaches playPause() inside the
                  // gesture and plays.
                  void ws.play().catch(() => undefined);
                }}
              />
            </div>
          )}
        </div>
      </div>
      {!!parsedContent && (
        <>
          <div className={styles.dividerHolder}>
            <Divider orientation="horizontal" size="100%" />
          </div>
          <div className={styles.audioReadMore}>
            <div className={contentClassName}>{!!onReadMore ? parsedContent.slice(0, 200) : parsedContent}</div>
            {!!onReadMore && (
              <button type="button" onClick={onReadMore}>
                <span>Read more</span>
                <Icon type="menu-11" size={12} />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

interface AudioPreviewProps {
  previewUrl?: string;
  waveformSeed: string;
  canLoadAudio: boolean;
  isAudioLoading: boolean;
  onReadMore?: () => void;
  parsedContent?: string | null;
  playbackEnabled?: boolean;
  onRequestPlayback?: () => void | Promise<void>;
  onLoadError?: () => void;
  onDurationChange?: (duration: number) => void;
}

// The bars never shrink (see context-item.module.scss), so the count sets the minimum width the
// waveform needs: 60 bars at 1px = 60px, which fits even the narrowest source panel. Raising it
// past what a card can hold would push bars out of the clipped row instead of spacing them out.
const DECORATIVE_BAR_COUNT = 60;

const buildDecorativeWaveform = (seed: string): number[] => {
  let state = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    state = Math.imul(state ^ seed.charCodeAt(index), 16777619);
  }

  return Array.from({ length: DECORATIVE_BAR_COUNT }, () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    const normalized = ((value ^ (value >>> 14)) >>> 0) / 4294967296;
    return 4 + Math.round(normalized * 12);
  });
};
